import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
  TextInput,
  ActivityIndicator,
  Alert,
} from 'react-native';
import { ms, s, vs } from '../../../../utils/responsive';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { BottomNavBar } from '../../../../components/BottomNavBar';
import { RootStackNavigationProp, RootStackParamList } from '../../../../types/navigation';
import { medicationService } from '../../../../services/medication';

import { createLogger } from '../../../../utils/logger';
const logger = createLogger('MedicationsInput');

type MedicationsInputScreenRouteProp = RouteProp<RootStackParamList, 'MedicationsInput'>;

export const MedicationsInputScreen: React.FC = () => {
  const navigation = useNavigation<RootStackNavigationProp>();
  const route = useRoute<MedicationsInputScreenRouteProp>();

  const { medicationData, isEditing } = route.params || {};

  const [medicationName, setMedicationName] = useState(medicationData?.medicationName || '');
  const [dosage, setDosage] = useState(medicationData?.dosage || '');
  const [frequency, setFrequency] = useState(medicationData?.frequency || '');
  const [prescribingDoctor, setPrescribingDoctor] = useState(medicationData?.prescribingDoctor || '');
  const [pharmacy, setPharmacy] = useState(medicationData?.pharmacy || '');
  const [conditionTreated, setConditionTreated] = useState(medicationData?.conditionTreated || '');
  const [notes, setNotes] = useState(medicationData?.notes || '');
  const [saving, setSaving] = useState(false);
  const [showAddAnotherDone, setShowAddAnotherDone] = useState(false);
  const [savedMedicationId, setSavedMedicationId] = useState<string | undefined>();

  const handleSave = async () => {
    // Validation - require at least medication name
    if (!medicationName.trim()) {
      Alert.alert('Error', 'Please enter at least a medication name');
      return;
    }

    try {
      setSaving(true);

      const medicationUpdateData = {
        taskId: 9, // Medication task ID
        medicationName: medicationName.trim(),
        dosage: dosage.trim(),
        frequency: frequency.trim(),
        prescribingDoctor: prescribingDoctor.trim(),
        pharmacy: pharmacy.trim(),
        conditionTreated: conditionTreated.trim(),
        notes: notes.trim(),
      };

      let savedMedication;

      if (isEditing && medicationData?.id) {
        // Update existing record
        savedMedication = await medicationService.updateMedication(
          medicationData.id,
          medicationUpdateData
        );
        // Handle wrapped response format { success, data, message }
        const updatedData = savedMedication?.data || savedMedication;
        logger.debug('Medication updated successfully with ID:', updatedData.id);

        // Navigate to lock/unlock screen to allow re-selection
        navigation.navigate('MedicationsLockUnlock', { medicationId: updatedData.id });
      } else {
        // Create new record
        savedMedication = await medicationService.createMedication(medicationUpdateData);
        // Handle wrapped response format { success, data, message }
        const createdData = savedMedication?.data || savedMedication;
        logger.debug('Medication saved successfully with ID:', createdData.id);

        // Store the saved ID for the Done button
        setSavedMedicationId(createdData.id);

        // Clear the form for Add Another
        setMedicationName('');
        setDosage('');
        setFrequency('');
        setPrescribingDoctor('');
        setPharmacy('');
        setConditionTreated('');
        setNotes('');

        // Show Add Another / Done buttons after successful save
        setShowAddAnotherDone(true);
        // Reset saving so buttons are interactive
        setSaving(false);
      }
    } catch (error) {
      logger.error('Error saving medication:', error);
      Alert.alert('Error', 'Failed to save medication information. Please try again.');
      setSaving(false);
    }
  };

  const handleAddAnother = () => {
    // Clear form and hide buttons
    setMedicationName('');
    setDosage('');
    setFrequency('');
    setPrescribingDoctor('');
    setPharmacy('');
    setConditionTreated('');
    setNotes('');
    setShowAddAnotherDone(false);
    setSaving(false);
  };

  const handleDone = async () => {
    setSaving(false);
    logger.debug('[Done Debug] Using ID:', savedMedicationId);

    // If we don't have a saved ID, get the most recent medication entry
    if (!savedMedicationId) {
      try {
        const response = await medicationService.getMedications(9);
        logger.debug('[Done Debug] API response:', response);

        // Handle response format - check if it's wrapped in a success/data object
        const allMedications = response?.data || response;
        logger.debug('[Done Debug] Extracted medications array:', allMedications);

        const mostRecent = allMedications && Array.isArray(allMedications) && allMedications.length > 0 ? allMedications[allMedications.length - 1] : null;
        if (mostRecent) {
          logger.debug('[Done Debug] Using most recent ID:', mostRecent.id);
          navigation.navigate('MedicationsLockUnlock', { medicationId: mostRecent.id });
        } else {
          logger.debug('[Done Debug] No medication entries found in array:', allMedications);
          Alert.alert('Error', 'No medication information found. Please create an entry first.');
        }
      } catch (error) {
        logger.error('[Done Debug] Error getting medications:', error);
        Alert.alert('Error', 'Failed to get medication information.');
      }
    } else {
      navigation.navigate('MedicationsLockUnlock', { medicationId: savedMedicationId });
    }
  };

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Image
            source={require('../../../../assets/icons/back.png')}
            style={[styles.backIcon, { tintColor: '#fff' }]}
          />
        </TouchableOpacity>
      </View>

      <View style={styles.topSection}>
        <Image
          source={require('../../../../assets/icons/keyimage.png')}
          style={styles.backgroundKey}
        />
        <Image
          source={require('../../../../assets/logo.png')}
          style={styles.logo}
          resizeMode="contain"
        />
        <Text style={styles.title}>
          Add Your Medication Information
        </Text>
      </View>

      <View style={styles.inputContainer}>
        <ScrollView style={styles.scrollView} contentContainerStyle={styles.scrollViewContent} keyboardShouldPersistTaps="handled" automaticallyAdjustKeyboardInsets={true}>
          <View style={styles.inputGroup}>
            <Text style={styles.label}>Medication Name *</Text>
            <TextInput
              style={styles.input}
              value={medicationName}
              onChangeText={setMedicationName}
              placeholder="Ex: Lisinopril, Metformin, etc."
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Dosage</Text>
            <TextInput
              style={styles.input}
              value={dosage}
              onChangeText={setDosage}
              placeholder="Ex: 10mg, 500mg, etc."
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Frequency</Text>
            <TextInput
              style={styles.input}
              value={frequency}
              onChangeText={setFrequency}
              placeholder="Ex: Once daily, Twice daily, etc."
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Prescribing Doctor</Text>
            <TextInput
              style={styles.input}
              value={prescribingDoctor}
              onChangeText={setPrescribingDoctor}
              placeholder="Dr. Smith"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Pharmacy</Text>
            <TextInput
              style={styles.input}
              value={pharmacy}
              onChangeText={setPharmacy}
              placeholder="CVS, Walgreens, etc."
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Condition Treated</Text>
            <TextInput
              style={styles.input}
              value={conditionTreated}
              onChangeText={setConditionTreated}
              placeholder="What condition is this medication for?"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Notes</Text>
            <TextInput
              style={[styles.input, styles.textArea]}
              value={notes}
              onChangeText={setNotes}
              placeholder="Additional notes about this medication"
              placeholderTextColor="#A9A9A9"
              multiline
              numberOfLines={4}
              textAlignVertical="top"
            />
          </View>

          {showAddAnotherDone ? (
            <View style={styles.addAnotherDoneContainer}>
              <TouchableOpacity
                style={styles.addAnotherButton}
                onPress={handleAddAnother}
              >
                <Text style={styles.submitButtonText}>Add Another</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.doneButton}
                onPress={handleDone}
              >
                <Text style={styles.submitButtonText}>Done</Text>
              </TouchableOpacity>
            </View>
          ) : (
            <View style={styles.submitButtonContainer}>
              <TouchableOpacity
                style={[styles.saveButton, saving && { opacity: 0.7 }]}
                onPress={handleSave}
                disabled={saving}
              >
                {saving ? (
                  <ActivityIndicator color="#FFFFFF" />
                ) : (
                  <Text style={styles.saveButtonText}>{isEditing ? 'Update' : 'Submit'}</Text>
                )}
              </TouchableOpacity>
            </View>
          )}

          <View style={styles.bottomPadding} />
        </ScrollView>
      </View>

      <BottomNavBar />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: ms(15),
    paddingTop: ms(25),
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: ms(50),
    padding: ms(15),
    paddingTop: ms(50),
    zIndex: 1,
  },
  backIcon: {
    width: s(32),
    height: s(32),
    tintColor: '#fff',
  },
  topSection: {
    alignItems: 'center',
    paddingVertical: ms(20),
  },
  backgroundKey: {
    position: 'absolute',
    width: '85%',
    height: '85%',
    opacity: 0.1,
  },
  logo: {
    width: s(125),
    height: s(125),
    marginBottom: ms(16),
  },
  title: {
    fontSize: ms(24, 0.3),
    fontWeight: 'bold',
    color: '#FFFFFF',
    marginBottom: ms(8),
    paddingHorizontal: ms(30),
    textAlign: 'center',
  },
  inputContainer: {
    flex: 1,
    backgroundColor: '#F3F6F8',
    borderTopLeftRadius: ms(20),
    borderTopRightRadius: ms(20),
    paddingTop: ms(20),
  },
  scrollView: {
    paddingHorizontal: ms(16),
    paddingBottom: ms(120),
  },
  scrollViewContent: {
    paddingBottom: ms(350),
  },
  inputGroup: {
    marginBottom: ms(16),
  },
  label: {
    fontSize: ms(16, 0.3),
    fontWeight: '600',
    color: '#6B7280',
    marginBottom: ms(8),
  },
  input: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(25),
    padding: ms(15),
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
    borderWidth: ms(1),
    borderColor: '#d8dbdd',
  },
  textArea: {
    minHeight: s(80),
    textAlignVertical: 'top',
  },
  submitButtonContainer: {
    alignItems: 'center',
    marginTop: ms(20),
    marginBottom: ms(40),
  },
  saveButton: {
    backgroundColor: '#29BFC0',
    borderRadius: ms(25),
    paddingVertical: ms(15),
    paddingHorizontal: ms(60),
    alignItems: 'center',
    marginBottom: ms(120),
    minWidth: s(200),
  },
  saveButtonText: {
    color: '#FFFFFF',
    fontSize: ms(16, 0.3),
    fontWeight: '600',
  },
  submitButtonText: {
    color: '#FFFFFF',
    fontSize: ms(16, 0.3),
    fontWeight: '600',
  },
  addAnotherDoneContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: ms(20),
    marginBottom: ms(120),
    gap: ms(20),
  },
  addAnotherButton: {
    backgroundColor: '#FF9F5A',
    borderRadius: ms(25),
    paddingVertical: ms(15),
    paddingHorizontal: ms(30),
    alignItems: 'center',
    minWidth: s(140),
  },
  doneButton: {
    backgroundColor: '#29BFC0',
    borderRadius: ms(25),
    paddingVertical: ms(15),
    paddingHorizontal: ms(30),
    alignItems: 'center',
    minWidth: s(140),
  },
  bottomPadding: {
    height: s(120),
  },
});
